import React, { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ExpirationTimer from "./ExpirationTimer";
import { FaArrowLeft } from "react-icons/fa6";
import {
  useCommonVerifyOtpMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
} from "../../api/api";
import { useToast } from "../../@/components/ui/use-toast";
import { validateField } from "../../lib/utils";
import { setToken } from "../../redux/slices/authSlice";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "../../@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import {
  MIN_PASSWORD_LENGTH,
  OTP_TIME_MILLISEC,
  REGEXES,
} from "../../lib/constants";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

interface FormData {
  email: string;
  otp: string;
  newPassword: string;
  confirmPassword: string;
}

export const ForgotPassword: React.FC = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const {
    setError,
    register,
    watch,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    clearErrors,
  } = useForm<FormData>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isAdmin = queryParams.get("isAdmin");
  const [timeLeft, setTimeLeft] = useState<number>(OTP_TIME_MILLISEC);
  const [forgotPasswordApi, { isLoading: isSendingOtp }] =
    useForgotPasswordMutation();
  const [verifyOtpApi, { isLoading: isVerifyingOtp }] =
    useCommonVerifyOtpMutation();
  const [verifyPassword, { isLoading: isResettingPassword }] =
    useResetPasswordMutation();

  const { toast } = useToast();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    clearErrors(name as keyof FormData);
  };

  const onSubmitEmail: SubmitHandler<FormData> = async (data) => {
    localStorage.removeItem("startTime");
    setEmail(data.email.toLowerCase());
    const emailError = validateField("email", data.email, { required: true });
    if (emailError) {
      setError("email", {
        type: "manual",
        message: emailError,
      });
      return;
    }
    try {
      const response = await forgotPasswordApi({
        body: {
          emailOrPhone: data.email.toLowerCase(),
          role: isAdmin ? "ADMIN" : "VENDOR",
        },
      }).unwrap();
      if (response.message === "OTP sent successfully") {
        toast({
          title: "Success",
          description: "OTP sent successfully.",
        });
        setStep(2);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: `${error?.data?.message}`,
      });
    }
  };

  const onResendOtp = async () => {
    try {
      const email = watch("email");
      await forgotPasswordApi({
        body: {
          emailOrPhone: email.toLowerCase(),
          role: isAdmin ? "ADMIN" : "VENDOR",
        },
      }).unwrap();
      toast({
        title: "Success",
        description: "OTP resent successfully.",
      });
      setTimeLeft(OTP_TIME_MILLISEC);
    } catch (error: any) {
      toast({
        title: "Error",
        description: `${error?.data?.message}`,
      });
    }
  };

  const onSubmitOtp: SubmitHandler<FormData> = async (data) => {
    const otpError = validateField("otp", data.otp, { required: true });
    if (otpError) {
      setError("otp", {
        type: "manual",
        message: otpError,
      });
      return;
    }
    try {
      const response = await verifyOtpApi({
        body: {
          emailOrPhone: watch("email"),
          role: isAdmin ? "ADMIN" : "VENDOR",
          otp: parseInt(data.otp),
        },
      }).unwrap();
      if (response.token) {
        dispatch(setToken(response.token)); //Issue resides here
        toast({
          title: "Success",
          description: "OTP verified successfully.",
        });
        setStep(3);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: `${error?.data?.message}`,
      });
    }
  };

  const onSubmitPassword: SubmitHandler<FormData> = async (data) => {
    try {
      const response = await verifyPassword({
        body: {
          role: isAdmin ? "ADMIN" : "VENDOR",
          newPassword: data.newPassword,
        },
      }).unwrap();
      if (response.token) {
        toast({
          title: "Success",
          description: `${response?.message}`,
        });
      }
      if (isAdmin) {
        navigate("/dashboard");
      } else {
        navigate("/vendor-login");
      }
    } catch (error: any) {
      console.error(error);
      toast({
        title: "Error",
        description: `${
          error?.data === undefined
            ? "Something went wrong, please try again later"
            : error?.data?.message
        }`,
      });
    }
  };

  const handleOtpChange = (newOtp: string) => {
    setError("otp", { type: "manual", message: "" });
    const otpError = validateField("otp", newOtp, { required: true });
    if (otpError) {
      setError("otp", {
        type: "manual",
        message: otpError,
      });
      return;
    }
    setValue("otp", newOtp);
  };

  return (
    <div className="flex min-h-max select-none flex-col md:flex-row bg-white overflow-y-auto">
      <div className="w-full bg-white flex-1">
        <div className="xl:px-[38.5vw] md:px-[33vw] sm:px-[20vw] px-[10vw] xl:py-[23vh] py-[20vh]">
          <div className="text-start mb-8">
            <img
              src="/Images/LoginPageImages/Auth-Page-Logo.png"
              alt="Auth-Page-Logo"
              className="w-36 font-bold mb-3"
            />
          </div>

          {step === 1 && (
            <>
              <h1 className="text-[3.5vh] mt-6 font-helveticaMedium text-customGrayDark">
                Reset your password
              </h1>
              <p className="text-customGrayBase leading-relaxed text-[1.8vh] mb-6">
                Enter the email address you used to register with.
              </p>
              <form onSubmit={handleSubmit(onSubmitEmail)}>
                <label
                  htmlFor="email"
                  className="block text-[1.6vh] font-helveticaMedium text-customGrayBase"
                >
                  Enter Email Address<span className="text-red-500">*</span>
                </label>
                <div className="relative mt-1">
                  <input
                    {...register("email", { required: "Email is required" })}
                    type="email"
                    className={`mt-1 text-sm block w-full px-[1vw] py-[1vh] border ${
                      errors.email ? "border-red-500" : "border-gray-300"
                    } focus:border-customGreenColor focus:ring-1 border focus:outline-customGreenColor rounded-md`}
                    placeholder="Email"
                    id="email"
                    name="email"
                  />
                  <div
                    className="absolute bg-transparent inset-y-0 -right-1 flex items-center px-3"
                    style={{ top: "50%", transform: "translateY(-50%)" }}
                  >
                    <img
                      src="./Images/LoginPageImages/shop.png"
                      className="object-fill text-customGreenColor lg:w-[1.25vw] lg:h-[1.25vw] w-4 h-4 sm:w-5 sm:h-5"
                      alt="Shop Icon"
                    />
                  </div>
                </div>
                {errors.email && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.email.message}
                  </p>
                )}
                {/* <p className='text-[1.55vh] text-customBlueColor mt-[2vh]'>Forgot or lost your email address?</p> */}
                <div className="grid lg:grid-cols-2 align-baseline grid-cols-1 gap-3 mt-[8vh] w-full">
                  <button
                    className="col-span-1 w-full bg-customGreenLight text-customGreenColor p-3 font-helveticaMedium flex flex-row justify-center items-center rounded-md space-x-2 text-[1.8vh]"
                    onClick={() => {
                      if (isAdmin) {
                        navigate("/admin-login");
                      } else {
                        navigate("/vendor-login");
                      }
                    }}
                  >
                    <FaArrowLeft className="mr-1 xxxl:w-5 xxxl:h-5 xxl:w-4 xxl:h-4 w-3 h-3" />
                    Back to sign in
                  </button>

                  <button
                    disabled={isSendingOtp}
                    type="submit"
                    className="col-span-1 flex p-3 items-center justify-center w-full font-helveticaMedium rounded-md text-[1.8vh] text-white bg-customGreenColor hover:bg-customGreenColor focus:outline-none focus:ring-1 focus:ring-offset-2 transition duration-150 ease-in-out"
                  >
                    {isSendingOtp ? <div className="loader" /> : "Send OTP"}
                  </button>
                  <p className="lg:col-span-2 col-span-1 mt-1 tracking-wide text-customGrayBase text-[1.8vh]">
                    © Copyright@{new Date().getFullYear()} Afrocart. All rights
                    reserved
                  </p>
                </div>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <h1 className="text-[3.5vh] mt-6 font-helveticaMedium text-customGrayDark">
                Reset your password
              </h1>
              <p className="text-customGrayBase leading-relaxed text-[1.8vh] mb-6">
                We sent a code to{" "}
                <span className="text-customGreenColor font-helveticaMedium">
                  {email}
                </span>
              </p>
              <form
                onSubmit={handleSubmit(onSubmitOtp)}
                className="grid grid-cols-1"
              >
                <ExpirationTimer
                  key={timeLeft}
                  expiryTime={OTP_TIME_MILLISEC}
                  setTimeLeft={setTimeLeft}
                  timeLeft={timeLeft}
                />
                <InputOTP
                  maxLength={6}
                  pattern={REGEXP_ONLY_DIGITS}
                  onChange={handleOtpChange}
                  className={`${
                    errors.otp ? "border-red-500" : "border-gray-300"
                  } -mt-2 col-span-1 gap-x-1 w-full`}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSeparator />
                    <InputOTPSlot index={1} />
                    <InputOTPSeparator />
                    <InputOTPSlot index={2} />
                    <InputOTPSeparator />
                    <InputOTPSlot index={3} />
                    <InputOTPSeparator />
                    <InputOTPSlot index={4} />
                    <InputOTPSeparator />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
                <input
                  {...register("otp", { required: "Please enter a valid OTP" })}
                  type="hidden"
                  id="otp"
                  name="otp"
                />
                {errors.otp && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.otp.message}
                  </p>
                )}
                <button
                  disabled={isVerifyingOtp}
                  type="submit"
                  className="mt-8 flex p-3 items-center justify-center w-full font-helveticaMedium rounded-md text-[1.8vh] text-white bg-customGreenColor hover:bg-customGreenColor focus:outline-none focus:ring-1 focus:ring-offset-2 transition duration-150 ease-in-out"
                >
                  {isVerifyingOtp ? <div className="loader" /> : "Continue"}
                </button>
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={onResendOtp}
                    className={`text-customGrayBase text-[1.8vh] font-helveticaMedium ${
                      isSendingOtp || timeLeft > 0
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    disabled={isSendingOtp || timeLeft > 0}
                  >
                    I didn’t receive the code!
                    <span
                      className={`${
                        isSendingOtp || timeLeft > 0
                          ? "text-customGreenDisabled"
                          : "text-customGreenColor"
                      }`}
                    >
                      {" "}
                      Resend code
                    </span>
                  </button>
                </div>
              </form>
            </>
          )}

          {step === 3 && (
            <>
              <h1 className="text-[3.5vh] mt-6 font-helveticaMedium text-customGrayDark">
                Set new password
              </h1>
              <p className="text-customGrayBase leading-relaxed text-[1.8vh] mb-6">
                Must be at least 8 characters
              </p>

              <form
                onSubmit={handleSubmit(onSubmitPassword)}
                className="grid grid-cols-1"
              >
                <label
                  htmlFor="newPassword"
                  className="block text-[1.6vh] font-helveticaMedium text-customGrayBase"
                >
                  New Password
                </label>
                <div className="relative">
                  <input
                    autoComplete="new-password"
                    type={showPassword ? "text" : "password"}
                    className={`mt-1 text-sm block w-full px-[1vw] py-[1vh] border ${
                      errors.newPassword ? "border-red-500" : "border-gray-300"
                    } focus:border-customGreenColor focus:ring-1 border focus:outline-customGreenColor rounded-md`}
                    {...register("newPassword", {
                      required: "This field is required",
                      validate: (value) => {
                        if (value.length < MIN_PASSWORD_LENGTH)
                          return "Password must be at least 8 characters";

                        return (
                          value === undefined ||
                          value == "" ||
                          (REGEXES.PASSWORD.test(value)
                            ? true
                            : "Password must include both upper and lower case characters")
                        );
                      },
                    })}
                    onChange={handleChange}
                  />
                  <div
                    className="absolute inset-y-0 top-1/2 transform -translate-y-[40%] right-0 flex items-center px-3 cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <AiOutlineEyeInvisible className=" lg:w-[1.25vw] lg:h-[1.25vw] w-4 h-4 sm:w-5 sm:h-5 text-customGrayBase" />
                    ) : (
                      <AiOutlineEye className="xxl:text-2xl text-xl text-customGrayBase" />
                    )}
                  </div>
                </div>
                {errors?.newPassword && (
                  <p className="text-red-600 text-sm">
                    {errors?.newPassword?.message}
                  </p>
                )}

                <label
                  htmlFor="confirmPassword"
                  className="mt-3 block text-[1.6vh] font-helveticaMedium text-customGrayBase"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    autoComplete="new-password"
                    type={showConfirmPassword ? "text" : "password"}
                    className={`mt-1 text-sm block w-full px-[1vw] py-[1vh] border ${
                      errors.confirmPassword
                        ? "border-red-500"
                        : "border-gray-300"
                    } focus:border-customGreenColor focus:ring-1 border focus:outline-customGreenColor rounded-md`}
                    {...register("confirmPassword", {
                      required: "This field is required",
                      validate: (value) => {
                        return (
                          value === getValues("newPassword") ||
                          "Passwords do not match"
                        );
                      },
                    })}
                    onChange={handleChange}
                  />
                  <div
                    className="absolute inset-y-0 top-1/2 transform -translate-y-[40%] right-0 flex items-center px-3 cursor-pointer"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <AiOutlineEyeInvisible className=" lg:w-[1.25vw] lg:h-[1.25vw] w-4 h-4 sm:w-5 sm:h-5 text-customGrayBase" />
                    ) : (
                      <AiOutlineEye className="xxl:text-2xl text-xl text-customGrayBase" />
                    )}
                  </div>
                </div>
                {errors?.confirmPassword && (
                  <p className="text-red-600 text-sm">
                    {errors?.confirmPassword?.message}
                  </p>
                )}

                <div className="grid lg:grid-cols-2 align-baseline grid-cols-1 gap-3 mt-[8vh] w-full">
                  <button
                    type="button"
                    disabled
                    className="col-span-1 w-full bg-customGreenLight text-customGreenColor p-3 font-helveticaMedium flex flex-row justify-center items-center rounded-md space-x-2 text-[1.8vh]"
                  >
                    <FaArrowLeft className="mr-1 xxxl:w-5 xxxl:h-5 xxl:w-4 xxl:h-4 w-3 h-3" />
                    Back to sign in
                  </button>

                  <button
                    disabled={isResettingPassword}
                    type="submit"
                    className="col-span-1 flex p-3 items-center justify-center w-full font-helveticaMedium rounded-md text-[1.8vh] text-white bg-customGreenColor hover:bg-customGreenColor focus:outline-none focus:ring-1 focus:ring-offset-2 transition duration-150 ease-in-out"
                  >
                    {isResettingPassword ? (
                      <div className="loader" />
                    ) : (
                      "Reset Password"
                    )}
                  </button>
                  <p className="lg:col-span-2 col-span-1 mt-1 tracking-wide text-customGrayBase text-[1.8vh]">
                    © Copyright@{new Date().getFullYear()} Afrocart. All rights
                    reserved
                  </p>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
